import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import {
  catchError,
  defer,
  finalize,
  from,
  Observable,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from "rxjs";
import { StoreService } from "../store";
import { SubriseUserDetails } from "../models/users/subrise-user.model";
import { PartnerUserApiService } from "./api/partner-user-api.service";
import { UiLanguageService } from "./ui-language.service";

const PARTNER_FIREBASE_SYNC_SESSION_KEY = 'subrise.partnerFirebaseSynced.v1';

/**
 * The signed-in partner's own profile, on the partner app API (`/users/me`) rather than the
 * back-office user endpoints `UserService` covers. It writes the same `currentUser` store slot,
 * so the shell renders a partner exactly as it renders a back-office user — unless the slot is
 * already owned by the back-office profile (`currentUserSource`), which is the case for a
 * reseller who also holds a back-office role.
 *
 * The partner endpoints need a Firebase-to-local user sync before `/users/me` will answer, which
 * is why this lives apart: the sync is posted once per browser session (remembered in
 * `sessionStorage`), concurrent callers — the bootstrap and the profile guards fire together —
 * share one in-flight request, and a save that changes the profile can force the sync again.
 */
@Injectable({
  providedIn: 'root'
})
export class PartnerUserService {

  private sharedPartnerSession$: Observable<SubriseUserDetails> | null = null;

  constructor(
    private store: StoreService,
    private partnerUserApiService: PartnerUserApiService,
    private uiLanguage: UiLanguageService
  ) { }

  /**
   * Loads the signed-in partner into `currentUser`. Callers racing each other share one request;
   * `forcePostSync` skips both the shared request and the session flag to re-post the Firebase
   * sync, which is what a profile save needs so the synced local user picks up the new values.
   */
  loadPartnerCurrentUser(options?: { forcePostSync?: boolean }): Observable<SubriseUserDetails> {
    if (options?.forcePostSync === true) {
      this.invalidatePartnerSessionInflight();
      return defer(() => {
        this.beginPartnerSessionLoad();
        this.clearPartnerSessionSyncFlag();
        return this.postSyncThenMeWithMeFallbackOnForbidden$();
      });
    }

    if (!this.sharedPartnerSession$) {
      this.sharedPartnerSession$ = defer(() => {
        this.beginPartnerSessionLoad();
        return this.resolvePartnerSessionDefault$();
      }).pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        finalize(() => {
          this.sharedPartnerSession$ = null;
        })
      );
    }
    return this.sharedPartnerSession$;
  }

  updatePartnerProfile(
    user: SubriseUserDetails,
    options?: { syncFirebaseAfterPartnerProfileSave?: boolean }
  ): Observable<SubriseUserDetails> {
    this.beginPartnerSessionLoad();
    return from(this.partnerUserApiService.updatePartnerMe(user)).pipe(
      switchMap(() =>
        options?.syncFirebaseAfterPartnerProfileSave === true
          ? this.loadPartnerCurrentUser({ forcePostSync: true })
          : this.fetchPartnerMe$()
      ),
      tap(() => console.debug('Partner profile updated')),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error updating partner profile:', error);
        throw error;
      })
    );
  }

  /**
   * Drops the in-flight session and the sync flag, so the next load re-posts the Firebase sync.
   * `currentUser` itself is cleared by `UserService.clear()`, which owns that slot.
   */
  clear(): void {
    this.invalidatePartnerSessionInflight();
    this.clearPartnerSessionSyncFlag();
  }

  private invalidatePartnerSessionInflight(): void {
    this.sharedPartnerSession$ = null;
  }

  private beginPartnerSessionLoad(): void {
    this.store.setState({ ...this.store.state, loading: true, error: null });
  }

  private hasPartnerSessionSyncFlag(): boolean {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(PARTNER_FIREBASE_SYNC_SESSION_KEY) === '1';
  }

  private clearPartnerSessionSyncFlag(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(PARTNER_FIREBASE_SYNC_SESSION_KEY);
    }
  }

  private setPartnerSessionSyncFlag(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(PARTNER_FIREBASE_SYNC_SESSION_KEY, '1');
    }
  }

  private resolvePartnerSessionDefault$(): Observable<SubriseUserDetails> {
    if (this.hasPartnerSessionSyncFlag()) {
      return this.fetchPartnerMe$().pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 404) {
            this.clearPartnerSessionSyncFlag();
            return this.postSyncThenMe$();
          }
          return throwError(() => error);
        })
      );
    }
    return this.postSyncThenMe$();
  }

  private postSyncThenMe$(): Observable<SubriseUserDetails> {
    return this.partnerUserApiService.syncPartnerFirebaseLocalUser().pipe(
      switchMap(() => this.fetchPartnerMe$()),
      tap(() => this.setPartnerSessionSyncFlag())
    );
  }

  private postSyncThenMeWithMeFallbackOnForbidden$(): Observable<SubriseUserDetails> {
    return this.postSyncThenMe$().pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 403) {
          return this.fetchPartnerMe$().pipe(tap(() => this.setPartnerSessionSyncFlag()));
        }
        return throwError(() => error);
      })
    );
  }

  private fetchPartnerMe$(): Observable<SubriseUserDetails> {
    return from(this.partnerUserApiService.getPartnerMe()).pipe(
      tap((currentUser) => {
        this.uiLanguage.applyAccountLanguage(currentUser);
        const state = { ...this.store.state };
        // A reseller who also holds a back-office role is a back-office user in the shell: that
        // profile owns `currentUser` and this load only exists for the Firebase sync. Anything
        // else — a pure partner, or a partner refreshing their own profile after a save — is this
        // service's own slot to write.
        if (state.currentUserSource === 'backoffice') {
          console.debug('Partner profile loaded, leaving currentUser to the back-office profile');
        } else {
          state.currentUser = currentUser;
          state.currentUserSource = 'partner';
        }
        state.loading = false;
        state.error = null;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading partner current user:', error);
        return throwError(() => error);
      })
    );
  }
}
